/**
 * Action creator : return a function for given action
 * @param  {string}     type     Action type
 * @param  {...objects} argNames Properties' names of action
 * @return {func}                Function that generate action with type and properties given the
 *                               params
 */
export const makeActionCreator =
    (type, ...argNames) =>
    (...args) => {
        const action = { type };
        argNames.forEach((arg, index) => {
            action[argNames[index]] = args[index];
        });

        return action;
    };

/**
 * Action creator : return the function and the constant for the given action
 * @param  {string} type   Action type
 * @param  {string} action Action function name
 * @param  {Array}  inputs Properties' names of action
 * @return {object}        An object containing both function and constant
 */
export const makeActionAndTypeCreator = (type, action, inputs = []) => ({
    [type]: type,
    [action]: makeActionCreator(type, ...inputs),
});

/**
 * Transform a name into a type name : SNAKE_UPPER_CASE
 * @param  {string} name A camel case action name
 * @return {string}      Snake upper case type name
 */
export const toTypeName = (name) =>
    String(name)
        .replace(/([A-Z])/g, '_$1')
        .toUpperCase();

/**
 * Action creator for an API call (request, success, fail)
 * @param  {string} camelCaseName CamelCase name of action : the action function name
 * @param  {Array}  inputs        Properties' names of request action
 * @param  {Array}  outputs       Properties' names of response action
 * @return {object}               An object container constants and functions for requesting API
 */
export const makeApiActionCreator = (camelCaseName, inputs = [], outputs = []) => {
    const typeName = toTypeName(camelCaseName);

    return {
        [typeName]: typeName,
        ...makeActionAndTypeCreator(`REQUESTED_${typeName}`, camelCaseName, inputs),
        ...makeActionAndTypeCreator(`RECEIVED_${typeName}`, `${camelCaseName}Received`, outputs),
        ...makeActionAndTypeCreator(`FAILED_${typeName}`, `${camelCaseName}Failed`, ['error']),
    };
};

/**
 * App's actions.
 */
export default {
    ...makeApiActionCreator('getHabitations', [], ['habitations']),
    ...makeApiActionCreator('createEstima'),
    ...makeApiActionCreator('getEstima', ['id', 'onFullFill'], ['estima']),
    ...makeApiActionCreator('getCity', ['cityName'], ['city']),
    ...makeApiActionCreator('getCitiesFromZip', ['zip'], ['places']),
    ...makeApiActionCreator('getAddresses', ['addressName', 'cityName', 'zip'], ['addresses']),
    ...makeApiActionCreator('signup'),
    ...makeApiActionCreator('emailExists', ['email'], ['emailExists']),
    ...makeApiActionCreator('checkZoneMedia', ['values'], ['isZoneMedia', 'isPhoneRequired']),
    ...makeActionAndTypeCreator('UPDATE_SELECTED_ADDRESS', 'updateSelectedAddress', [
        'addressName',
        'cityName',
        'zip',
    ]),
    ...makeActionAndTypeCreator('UPDATE_SELECTED_COORDINATES', 'updateSelectedCoordinates', [
        'coordinates',
    ]),
    ...makeActionAndTypeCreator('UPDATE_ADDRESS_MAP_COORDINATES', 'updateAddressMapCoordinates', [
        'mapCoordinates',
    ]),
    ...makeActionAndTypeCreator('UPDATE_SUGGESTED_ADDRESSES', 'updateSuggestedAddresses', [
        'addresses',
    ]),
    ...makeActionAndTypeCreator('PREVIOUS_STEP', 'previousStep'),
    ...makeActionAndTypeCreator('CHANGE_PROPERTY_ADDRESS', 'changePropertyAddress', ['place']),
    ...makeActionAndTypeCreator('NEXT_STEP', 'nextStep'),
    ...makeActionAndTypeCreator('CHANGE_STEP', 'changeStep', ['step']),
    ...makeActionAndTypeCreator('VALID_STEP', 'validStep', ['step', 'isValid']),
    ...makeActionAndTypeCreator('SET_LAST_STEP', 'setLastStep', ['isLast']),
    ...makeActionAndTypeCreator('GA_STEP', 'gaStep', ['step', 'event']),
    ...makeActionAndTypeCreator('GA_SUBMIT', 'gaSubmit', ['category', 'source', 'event']),
    ...makeActionAndTypeCreator('STEPS_READY', 'stepsReady'),
    ...makeActionAndTypeCreator('BAROMETRE_READY', 'barometreReady'),
    ...makeActionAndTypeCreator('GA_BAROMETRE_RETRIEVE_PHONE_STEP', 'gaBarometreRetrievePhoneStep'),
    ...makeActionAndTypeCreator('GO_TO_STEPS_FORM', 'goToStepsForm', ['values']),
    ...makeActionAndTypeCreator('SAVE_ESTIMA_ID', 'saveEstimaId', [
        'habitationId',
        'signedUsername',
    ]),
    ...makeActionAndTypeCreator('ADD_FIELD_HINT', 'addFieldHint', ['field', 'hint']),
    ...makeActionAndTypeCreator('REMOVE_FIELD_HINT', 'removeFieldHint', ['field']),
    ...makeActionAndTypeCreator('FAKE_ANIMATION_ENDED', 'fakeAnimationEnded'),

    /* Error */
    ...makeActionAndTypeCreator('SET_ERROR', 'setError', ['error']),
    ...makeActionAndTypeCreator('CLEAR_ERROR', 'clearError', ['raw']),

    /* Login actions */
    ...makeApiActionCreator('checkAuth', [], ['auth']),
    ...makeActionAndTypeCreator('GA_LOGIN', 'gaLogin', ['action']),
    ...makeActionAndTypeCreator('GA_LOGIN_EVENT', 'gaLoginEvent', ['action', 'event']),
    ...makeActionAndTypeCreator('RECEIVED_SIGNUP', 'receivedSignup'),

    /* GeoSuggest Actions */
    ...makeApiActionCreator('getGeoSuggestions', ['value'], ['suggestions']),
    ...makeActionAndTypeCreator('GEO_SUGGEST_FIELD_VALUE_CHANGED', 'geoSuggestFieldValueChanged', [
        'value',
    ]),
    ...makeActionAndTypeCreator(
        'GEO_SUGGEST_FIELD_SUGGESTION_SELECTED',
        'geoSuggestFieldSuggestionSelected',
        ['value'],
    ),
    ...makeActionAndTypeCreator('CLEAR_GEO_SUGGESTIONS', 'clearGeoSuggestions'),
};
